import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from '../../components/Button';
import { DataTable } from '../../components/DataTable';
import { useApi } from '../../hooks/useApi';
import { useToast } from '../../hooks/useToast';
import {
  ApiError,
  listCoachCallbacks,
  markCoachCallbackContacted,
  type CoachCallback,
  type CoachCallbackListParams,
} from '../../lib/api';
import { formatLocal } from '../../lib/time';
import { PS, T } from '../../lib/theme';

const PAGE_SIZE = 25;
type StatusFilter = NonNullable<CoachCallbackListParams['status']>;

const FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'new', label: 'New' }, { value: 'contacted', label: 'Contacted' }, { value: 'all', label: 'All' },
];

function loadErrorMessage(error: Error): string {
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
    return 'You are not authorized to view callback requests.';
  }
  return 'Could not load callback requests.';
}

function emptyLabel(status: StatusFilter): string {
  if (status === 'new') return 'No new callback requests.';
  if (status === 'contacted') return 'No contacted callback requests.';
  return 'No callback requests.';
}

function displayTimestamp(value: string): string {
  return formatLocal(value, 'd MMM yyyy, HH:mm');
}

export function CoachCallbacksPage() {
  const [status, setStatus] = useState<StatusFilter>('new');
  const [offset, setOffset] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const { push } = useToast();
  const loader = useCallback(
    (signal: AbortSignal) => listCoachCallbacks({ status, limit: PAGE_SIZE, offset }, signal),
    [offset, status],
  );
  const callbacks = useApi(loader);

  useEffect(() => {
    if (!callbacks.data || offset === 0 || callbacks.data.total > offset) return;
    const lastPageOffset = Math.max(0, Math.floor((callbacks.data.total - 1) / PAGE_SIZE) * PAGE_SIZE);
    setOffset(lastPageOffset);
  }, [callbacks.data, offset]);

  const markContacted = useCallback(async (request: CoachCallback) => {
    setBusyId(request.request_id);
    setActionError(null);
    try {
      await markCoachCallbackContacted(request.request_id);
      push('success', 'Callback marked contacted.');
      callbacks.refetch();
    } catch {
      setActionError(`Could not mark ${request.name} contacted. Please try again.`);
    } finally {
      setBusyId(null);
    }
  }, [callbacks, push]);

  const columns = useMemo<ColumnDef<CoachCallback, unknown>[]>(() => [
    { accessorKey: 'name', header: 'Visitor', cell: ({ row }) => <span style={nowrapStyle}>{row.original.name}</span> },
    { accessorKey: 'phone', header: 'Phone', cell: ({ row }) => <span style={nowrapStyle}>{row.original.phone}</span> },
    { accessorKey: 'coach_name', header: 'Coach' },
    {
      accessorKey: 'source',
      header: 'Source',
      cell: () => 'Landing coaches',
    },
    { accessorKey: 'lang', header: 'Language', cell: ({ row }) => row.original.lang.toUpperCase() },
    {
      accessorKey: 'created_at',
      header: 'Created',
      cell: ({ row }) => <span style={nowrapStyle}>{displayTimestamp(row.original.created_at)}</span>,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const request = row.original;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
            <span style={request.status === 'new' ? newStatusStyle : contactedStatusStyle}>
              {request.status === 'new' ? 'New' : 'Contacted'}
            </span>
            {request.contacted_at && (
              <span style={{ ...T.caption, color: PS.textSec, whiteSpace: 'nowrap' }}>
                {displayTimestamp(request.contacted_at)}
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => row.original.status === 'new' ? (
        <Button
          type="button"
          variant="secondary"
          disabled={busyId !== null}
          onClick={() => void markContacted(row.original)}
          style={{ minHeight: 40, whiteSpace: 'nowrap' }}
        >
          {busyId === row.original.request_id ? 'Marking…' : 'Mark contacted'}
        </Button>
      ) : '—',
    },
  ], [busyId, markContacted]);

  const total = callbacks.data?.total ?? 0;
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const canGoForward = callbacks.data !== null && offset + callbacks.data.items.length < total;

  return (
    <section
      aria-labelledby="callback-requests-heading"
      style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 1400 }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1 id="callback-requests-heading" style={{ ...T.sectionTitle, margin: 0 }}>
            Callback requests
          </h1>
          <p style={{ ...T.caption, color: PS.textSec, margin: '4px 0 0' }}>
            Private visitor requests, newest first.
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          disabled={callbacks.loading}
          onClick={() => {
            setActionError(null);
            callbacks.refetch();
          }}
          style={{ minHeight: 40 }}
        >
          Refresh
        </Button>
      </header>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, ...T.ui }}>
        Request status
        <select
          aria-label="Request status"
          value={status}
          onChange={(event) => {
            setStatus(event.target.value as StatusFilter);
            setOffset(0);
            setActionError(null);
          }}
          style={selectStyle}
        >
          {FILTERS.map((filter) => (
            <option key={filter.value} value={filter.value}>{filter.label}</option>
          ))}
        </select>
      </label>

      {callbacks.loading && (
        <p role="status" style={{ ...T.ui, color: PS.textSec, margin: 0 }}>
          Loading callback requests…
        </p>
      )}

      {!callbacks.loading && callbacks.error && (
        <div role="alert" style={errorStyle}>
          <span>{loadErrorMessage(callbacks.error)}</span>
          <Button type="button" variant="secondary" onClick={callbacks.refetch} style={{ minHeight: 40 }}>
            Retry
          </Button>
        </div>
      )}

      {actionError && <p role="alert" style={errorStyle}>{actionError}</p>}

      {!callbacks.loading && !callbacks.error && callbacks.data && (
        <>
          <p style={{ ...T.caption, color: PS.textSec, margin: 0 }}>
            {total} {total === 1 ? 'request' : 'requests'}
          </p>
          <DataTable
            columns={columns}
            data={callbacks.data.items}
            emptyLabel={emptyLabel(status)}
          />
          <nav aria-label="Callback request pagination" style={paginationStyle}>
            <Button
              type="button"
              variant="secondary"
              disabled={offset === 0}
              onClick={() => setOffset((current) => Math.max(0, current - PAGE_SIZE))}
              style={{ minHeight: 40 }}
            >
              Previous page
            </Button>
            <span style={{ ...T.ui, color: PS.textSec }}>Page {page} of {totalPages}</span>
            <Button
              type="button"
              variant="secondary"
              disabled={!canGoForward}
              onClick={() => setOffset((current) => current + PAGE_SIZE)}
              style={{ minHeight: 40 }}
            >
              Next page
            </Button>
          </nav>
        </>
      )}
    </section>
  );
}

const nowrapStyle: CSSProperties = { whiteSpace: 'nowrap' };

const statusStyle: CSSProperties = {
  display: 'inline-block', padding: '4px 8px', borderRadius: 3, ...T.caption, fontWeight: 700,
};

const newStatusStyle: CSSProperties = {
  ...statusStyle, color: PS.danger, background: PS.errorBg,
};

const contactedStatusStyle: CSSProperties = {
  ...statusStyle, color: PS.success, background: PS.successSoft,
};

const selectStyle: CSSProperties = {
  minHeight: 40, padding: '8px 12px', border: `1px solid ${PS.border}`,
  borderRadius: 3, background: PS.white, color: PS.nearBlack, ...T.ui,
};

const errorStyle: CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
  flexWrap: 'wrap', margin: 0, padding: 12, border: `1px solid ${PS.errorBorder}`,
  borderRadius: 3, background: PS.errorBg, color: PS.errorText, ...T.ui,
};

const paginationStyle: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
};
